import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const CLASSIFICATIONS = ["UNCLASSIFIED", "CONFIDENTIAL", "SECRET", "OMEGA"];
const STATUSES = ["ACTIVE", "REDACTED", "LOST"];

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function FolderDetail() {
  const { id } = useParams();
  const { isAdmin } = useAuth();
  const [folder, setFolder] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/folders/${id}`).then((r) => setFolder(r.data)).catch(() => setFolder(false));
  }, [id]);

  const startEdit = () => {
    setDraft({ ...folder });
    setEditing(true);
  };
  const cancel = () => {
    setEditing(false);
    setDraft(null);
  };
  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/folders/${id}`, draft);
      setFolder(res.data);
      setEditing(false);
      toast.success(`${id} sealed.`);
    } catch (e) {
      toast.error("Seal failed.");
    } finally {
      setSaving(false);
    }
  };

  const onUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1.2 * 1024 * 1024) {
      toast.error("Image too large (max ~1.2MB).");
      return;
    }
    const b64 = await fileToBase64(file);
    setDraft((d) => ({ ...d, images: [...(d.images || []), b64] }));
  };

  const removeImage = (idx) => {
    setDraft((d) => ({ ...d, images: d.images.filter((_, i) => i !== idx) }));
  };

  if (folder === null)
    return <div className="text-[#555] text-xs tracking-widest uppercase">::: retrieving file :::</div>;
  if (folder === false)
    return (
      <div className="border border-[#333] p-12 text-center">
        <div className="font-serif-display text-3xl mb-4">File not located.</div>
        <Link to="/archive" className="tiv-btn">Return to archive</Link>
      </div>
    );

  return (
    <div className="space-y-10" data-testid="folder-detail-page">
      <div className="text-[0.65rem] tracking-[0.35em] uppercase text-[#A3A3A3]">
        <Link to="/archive" className="hover:text-[#E3E1D9]">↖ Archive</Link>
        <span className="mx-3 text-[#555]">/</span>
        <span>{folder.id}</span>
      </div>

      <header className="grid grid-cols-1 lg:grid-cols-12 gap-8 border-b border-[#333] pb-10">
        <div className="lg:col-span-8 space-y-5">
          <div className="flex items-center gap-3">
            <span className="text-xs tracking-[0.3em] text-[#A3A3A3]">FOLDER</span>
            <span className="text-sm tracking-[0.3em] text-[#E3E1D9]">{folder.id}</span>
            <span className={`status-pill ${
              folder.status === "ACTIVE" ? "status-active" :
              folder.status === "REDACTED" ? "status-redacted" : "status-lost"
            }`}>{folder.status}</span>
          </div>

          {editing ? (
            <input
              className="tiv-input text-3xl"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="title..."
              data-testid="folder-edit-title"
            />
          ) : (
            <h1 className="font-serif-display text-4xl sm:text-5xl leading-tight tracking-tighter">
              {folder.title || <span className="text-[#555] italic">[ untitled ]</span>}
            </h1>
          )}
        </div>

        <aside className="lg:col-span-4 border border-[#333] p-5 space-y-3 text-xs font-mono-data bg-[#0a0a0a]">
          <div className="section-label">
            <span>Index Card</span>
            <span>{folder.id}</span>
          </div>
          {editing ? (
            <div className="space-y-3">
              <label className="block">
                <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Classification</div>
                <select
                  className="tiv-input"
                  value={draft.classification}
                  onChange={(e) => setDraft({ ...draft, classification: e.target.value })}
                  data-testid="folder-edit-classification"
                >
                  {CLASSIFICATIONS.map((c) => <option key={c} value={c} className="bg-[#0a0a0a]">{c}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Status</div>
                <select
                  className="tiv-input"
                  value={draft.status}
                  onChange={(e) => setDraft({ ...draft, status: e.target.value })}
                  data-testid="folder-edit-status"
                >
                  {STATUSES.map((c) => <option key={c} value={c} className="bg-[#0a0a0a]">{c}</option>)}
                </select>
              </label>
              <label className="block">
                <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">Date</div>
                <input
                  className="tiv-input"
                  value={draft.date}
                  onChange={(e) => setDraft({ ...draft, date: e.target.value })}
                  data-testid="folder-edit-date"
                  placeholder="e.g. MMXXIV.III.07"
                />
              </label>
            </div>
          ) : (
            <div className="space-y-2 text-[#A3A3A3]">
              <div className="flex justify-between"><span>Class</span><span className="text-[#E3E1D9]">{folder.classification}</span></div>
              <div className="flex justify-between"><span>Status</span><span className="text-[#E3E1D9]">{folder.status}</span></div>
              <div className="flex justify-between"><span>Date</span><span className="text-[#E3E1D9]">{folder.date || "—"}</span></div>
              <div className="flex justify-between"><span>Updated</span><span className="text-[#E3E1D9]">{folder.updated_at?.split("T")[0]}</span></div>
            </div>
          )}
        </aside>
      </header>

      {/* Body */}
      <section>
        <div className="section-label">
          <span>Testimony · Body</span>
          <span>§ folio</span>
        </div>
        {editing ? (
          <textarea
            className="tiv-textarea"
            value={draft.body}
            onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            placeholder="enter the recovered text..."
            data-testid="folder-edit-body"
          />
        ) : folder.body ? (
          <div className="text-base leading-loose whitespace-pre-wrap max-w-3xl">
            {folder.body}
          </div>
        ) : (
          <div className="border border-dashed border-[#333] p-10 text-center text-[#555] text-xs tracking-widest uppercase">
            ::: this folder is empty :::
          </div>
        )}
      </section>

      {/* Images */}
      <section>
        <div className="section-label">
          <span>Evidence · Plates</span>
          <span>{(editing ? draft.images : folder.images)?.length || 0}</span>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {(editing ? draft.images : folder.images || []).map((src, i) => (
            <div key={`${src.slice(-24)}-${i}`} className="border border-[#333] aspect-square overflow-hidden relative group" data-testid={`folder-img-${i}`}>
              <img src={src} alt={`plate-${i}`} className="w-full h-full object-cover tiv-img" />
              {editing && (
                <button
                  onClick={() => removeImage(i)}
                  className="absolute top-1 right-1 tiv-btn tiv-btn-danger text-[0.55rem] px-2 py-1 opacity-0 group-hover:opacity-100"
                  data-testid={`folder-img-remove-${i}`}
                >
                  Burn
                </button>
              )}
            </div>
          ))}
          {editing && (
            <label className="border border-dashed border-[#333] aspect-square flex items-center justify-center cursor-crosshair hover:border-[#E3E1D9]" data-testid="folder-img-upload">
              <input type="file" accept="image/*" className="hidden" onChange={onUploadImage} />
              <span className="text-xs tracking-[0.3em] uppercase text-[#A3A3A3]">+ Affix Plate</span>
            </label>
          )}
        </div>
      </section>

      {/* Secret Lore — locked compartment per folder (iter-18) */}
      <SecretLoreSection
        folder={folder}
        editing={editing}
        draft={draft}
        setDraft={setDraft}
        isAdmin={isAdmin}
      />

      {/* Admin actions */}
      {isAdmin && (
        <div className="flex gap-3 border-t border-[#333] pt-8">
          {editing ? (
            <>
              <button className="tiv-btn" onClick={save} disabled={saving} data-testid="folder-save-btn">
                {saving ? "Sealing..." : "Seal & Save"}
              </button>
              <button className="tiv-btn" onClick={cancel} data-testid="folder-cancel-btn">
                Discard
              </button>
            </>
          ) : (
            <button className="tiv-btn" onClick={startEdit} data-testid="folder-edit-btn">
              Break Seal · Edit
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================
// SecretLoreSection — per-folder hidden compartment.
// • Admin (non-edit mode): sees the lore directly; passphrase shown in a
//   subtle "set passphrase" hint.
// • Admin (edit mode): edits both the lore body and the passphrase inline.
// • Non-admin: only sees a locked sigil button when `has_secret_lore` is true.
//   Clicking expands a passphrase prompt; on success the lore reveals inline.
// ============================================================
function SecretLoreSection({ folder, editing, draft, setDraft, isAdmin }) {
  const [unlocked, setUnlocked] = useState(false);
  const [pass, setPass] = useState("");
  const [revealed, setRevealed] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Admins in edit mode get inline form
  if (isAdmin && editing) {
    return (
      <section data-testid="folder-secret-edit">
        <div className="section-label">
          <span>Secret Lore · Locked Compartment</span>
          <span>§ sub rosa</span>
        </div>
        <div className="border border-[#333] bg-[#0a0a0a] p-5 space-y-4">
          <label className="block">
            <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">
              Passphrase (leave both empty to remove the compartment)
            </div>
            <input
              type="text"
              className="tiv-input"
              value={draft.secret_lore_passphrase || ""}
              onChange={(e) => setDraft({ ...draft, secret_lore_passphrase: e.target.value })}
              placeholder="e.g. mother-of-pearl"
              data-testid="folder-secret-passphrase-input"
            />
          </label>
          <label className="block">
            <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#555] mb-1">
              Hidden lore (revealed when the passphrase is spoken)
            </div>
            <textarea
              className="tiv-textarea"
              value={draft.secret_lore || ""}
              onChange={(e) => setDraft({ ...draft, secret_lore: e.target.value })}
              placeholder="what only the right voice may read..."
              data-testid="folder-secret-lore-input"
            />
          </label>
        </div>
      </section>
    );
  }

  // Admins in view mode: see lore directly (cleartext from GET because backend bypasses for admin)
  if (isAdmin && folder.has_secret_lore) {
    return (
      <section data-testid="folder-secret-admin-view">
        <div className="section-label">
          <span>Secret Lore · Locked Compartment</span>
          <span>passphrase: {folder.secret_lore_passphrase || "—"}</span>
        </div>
        <div className="border border-[#333] bg-[#0a0a0a] p-6 text-base leading-loose whitespace-pre-wrap max-w-3xl">
          {folder.secret_lore}
        </div>
      </section>
    );
  }

  // Non-admins: no compartment configured → render nothing.
  if (!folder.has_secret_lore) return null;

  const tryUnlock = async () => {
    if (!pass.trim()) return;
    setSubmitting(true);
    setError("");
    try {
      const r = await api.post(`/folders/${folder.id}/secret-lore/access`, { passphrase: pass });
      setRevealed(r.data.lore || "");
      setUnlocked(true);
      toast.success("The compartment opens.");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Passphrase rejected.");
    } finally {
      setSubmitting(false);
    }
  };

  const onKey = (e) => { if (e.key === "Enter") tryUnlock(); };

  // Non-admin, has_secret_lore=true, locked
  if (!unlocked) {
    return (
      <section data-testid="folder-secret-locked">
        <div className="section-label">
          <span>· · ·</span>
          <span>§ sub rosa</span>
        </div>
        <div className="border border-[#333] bg-[#0a0a0a] p-8 text-center space-y-5">
          <div className="font-serif-display text-6xl text-[#555] leading-none">◌</div>
          <div className="text-[0.6rem] tracking-[0.4em] uppercase text-[#A3A3A3]">
            A compartment in this folder is sealed.
          </div>
          <div className="text-xs text-[#555] max-w-md mx-auto leading-relaxed">
            Speak the passphrase. The voice is the key.
          </div>
          <div className="max-w-sm mx-auto space-y-3" onKeyDown={onKey}>
            <input
              type="password"
              className="tiv-input"
              value={pass}
              onChange={(e) => setPass(e.target.value)}
              placeholder="passphrase..."
              data-testid="folder-secret-passphrase-prompt"
            />
            {error && (
              <div className="text-xs text-[#721C1C] border border-[#721C1C] p-2 tracking-wide" data-testid="folder-secret-error">
                {error}
              </div>
            )}
            <button
              className="tiv-btn w-full"
              type="button"
              onClick={tryUnlock}
              disabled={submitting || !pass.trim()}
              data-testid="folder-secret-unlock-btn"
            >
              {submitting ? "listening..." : "Speak"}
            </button>
          </div>
        </div>
      </section>
    );
  }

  // Non-admin, unlocked
  return (
    <section data-testid="folder-secret-revealed">
      <div className="section-label">
        <span>Secret Lore · Compartment Open</span>
        <span>§ sub rosa</span>
      </div>
      <div className="border border-[#E3E1D9] bg-[#0a0a0a] p-6 text-base leading-loose whitespace-pre-wrap max-w-3xl">
        {revealed}
      </div>
    </section>
  );
}
